import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Avatar, Chip, Button, TextField, MenuItem,
  Stack, CircularProgress, Divider, Tooltip, IconButton,
} from '@mui/material';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import LoginIcon           from '@mui/icons-material/Login';
import LogoutIcon          from '@mui/icons-material/Logout';
import CoffeeIcon          from '@mui/icons-material/Coffee';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import MoreTimeIcon        from '@mui/icons-material/MoreTime';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import PauseCircleIcon     from '@mui/icons-material/PauseCircle';
import PendingActionsIcon  from '@mui/icons-material/PendingActions';
import RefreshIcon         from '@mui/icons-material/Refresh';
import { useTranslation }  from 'react-i18next';
import apiService, { authFetch } from '../../services/apiServices';
import { getAuthHeaders }  from '../../services/authSession';
import { computeAttendanceSummary } from '../../services/attendanceDemoService';
import { API_URL, isDemoMode } from '../../services/backendMode';
import { activeWorkers } from '../../utils/workerStatus';

async function fetchAttendanceSummary(from, to) {
  if (isDemoMode()) {
    // Demo mode — llogarit direkt nga localStorage
    const data = computeAttendanceSummary({ from, to });
    return { success: true, data };
  }
  const qs = new URLSearchParams({ from, to }).toString();
  const res = await fetch(`${API_URL}/api/reports/attendance-summary?${qs}`, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.json().catch(() => ({ success: false, data: [] }));
}

// Dita LOKALE: `toISOString()` është UTC dhe para orës 02:00 jepte ditën e djeshme.
const localYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayStr = () => localYMD(new Date());

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });
}

function fmtMinutes(min) {
  if (min == null || isNaN(min)) return '—';
  const m = Math.max(0, Math.round(Number(min)));
  if (m === 0) return '0m';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

function initials(name) {
  return (name || '')
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function StatusChip({ status, lateMin, earlyMin, overtimeMin }) {
  const { t } = useTranslation();
  if (status === 'absent')       return <Chip label={t('report.absentLabel')}  color="error"   size="small" />;
  if (status === 'in_progress')  return <Chip label={t('report.inProgress')}   color="primary" size="small" icon={<PendingActionsIcon />} />;
  if (status === 'late') {
    const label = lateMin > 0 ? `${t('report.lateLabel')} ${fmtMinutes(lateMin)}` : t('report.lateLabel');
    return <Chip label={label} color="warning" size="small" icon={<WarningAmberIcon />} />;
  }
  if (status === 'early_checkout') {
    const label = earlyMin > 0 ? `${t('report.earlyCheckout')} ${fmtMinutes(earlyMin)}` : t('report.earlyCheckout');
    return <Chip label={label} color="warning" size="small" />;
  }
  if (status === 'on_time')      return <Chip label={t('report.onTime')}       color="success" size="small" icon={<CheckCircleIcon />} />;
  return <Chip label={status || '—'} size="small" />;
}

function SummaryCard({ title, value, icon, color }) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: 3, height: '100%' }} elevation={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.dark`, width: 48, height: 48 }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700} color={`${color}.main`}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function AttendanceDashboard() {
  const { t } = useTranslation();
  const [date,       setDate]       = useState(todayStr());
  const [sessions,   setSessions]   = useState([]);
  const [workers,    setWorkers]    = useState([]);
  const [departments,setDeps]       = useState([]);
  const [fDept,      setFDept]      = useState('');
  const [loading,    setLoading]    = useState(false);
  // Attendance Rate i ditës — nga serveri (services/attendanceRate.js), jo nga
  // numërimi i sesioneve: njeh orarin, pushimin e aprovuar, festat dhe punësimin.
  const [rate,       setRate]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, wRes, dRes] = await Promise.all([
        fetchAttendanceSummary(date, date),
        apiService().get('workers'),
        apiService().get('departments'),
      ]);
      setSessions(sRes?.data || []);
      setWorkers(wRes?.data || []);
      setDeps(dRes?.data || []);
      try {
        const r = await authFetch(`/api/reports/attendance-rate?period=daily&date=${encodeURIComponent(date)}`);
        const j = await r.json().catch(() => ({}));
        setRate(r.ok && j?.success ? j.data : null);
      } catch { setRate(null); }
    } catch (e) {
      console.error('[AttendanceDashboard]', e);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Filter by department
  const filtered = fDept
    ? sessions.filter((s) => String(s.department_id) === String(fDept))
    : sessions;

  // Workers who have no session for the day
  const workerIds = new Set(filtered.map((s) => String(s.worker_id)));
  // Emri i vjetër i kësaj liste ishte `activeWorkers`, por ajo filtronte vetëm
  // departamentin: te «Mungojnë sot» dilnin edhe njerëz të larguar prej vitesh.
  const deptWorkers = fDept
    ? workers.filter((w) => String(w.Department || w.department_id || '') === String(fDept))
    : workers;
  const absentWorkers = activeWorkers(deptWorkers).filter((w) => !workerIds.has(String(w.Id || w.id)));

  // Summary counts
  const present        = filtered.filter((s) => s.check_in).length;
  const onBreak        = filtered.filter((s) => s.status === 'on_break').length;
  const lateCount      = filtered.filter((s) => Number(s.late_minutes) > 0).length;
  const overtimeCount  = filtered.filter((s) => Number(s.overtime_minutes) > 0).length;

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap">
        <Typography variant="h5" fontWeight={700}>
          {t('report.presencePanel')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            type="date"
            size="small"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            sx={{ width: 160 }}
          />
          <TextField
            select
            size="small"
            value={fDept}
            onChange={(e) => setFDept(e.target.value)}
            sx={{ minWidth: 160 }}
            label={t('report.department')}
          >
            <MenuItem value="">{t('report.all')}</MenuItem>
            {departments.map((d) => (
              <MenuItem key={d.Id || d.id} value={String(d.Id || d.id)}>
                {d.Name || d.name}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={t('report.refresh')}>
            <IconButton onClick={load} size="small" disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            title={t('report.presentWorkers')}
            value={loading ? '…' : present}
            icon={<LoginIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            title={t('report.absentLabel')}
            value={loading ? '…' : absentWorkers.length}
            icon={<LogoutIcon />}
            color="error"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            title={t('report.latenessCard')}
            value={loading ? '…' : lateCount}
            icon={<WarningAmberIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            title={t('report.overtime')}
            value={loading ? '…' : overtimeCount}
            icon={<MoreTimeIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SummaryCard
            title="Attendance Rate (dita)"
            value={loading ? '…' : rate?.attendance_rate == null ? '—' : `${Number(rate.attendance_rate).toFixed(1)} %`}
            icon={<LoginIcon />}
            color={rate?.attendance_rate == null ? 'info' : rate.attendance_rate >= 95 ? 'success' : rate.attendance_rate >= 80 ? 'warning' : 'error'}
          />
          {rate && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
              {Number(rate.attended_hours).toFixed(1)} h të punuara nga {Number(rate.expected_hours).toFixed(1)} h të pritura · {rate.employee_count} punonjës
              {rate.leave_hours > 0 ? ` · leje ${Number(rate.leave_hours).toFixed(1)} h` : ''}
              {rate.holiday_hours > 0 ? ' · festë zyrtare' : ''}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Sessions Table */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('report.presenceDetails')} — {date}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>{t('report.worker')}</TableCell>
                  <TableCell>{t('report.department')}</TableCell>
                  <TableCell>{t('report.scheduleCol')}</TableCell>
                  <TableCell>{t('report.checkIn')}</TableCell>
                  <TableCell>{t('report.checkOut')}</TableCell>
                  <TableCell>{t('report.breakCol')}</TableCell>
                  <TableCell align="right">{t('report.netHoursCol')}</TableCell>
                  <TableCell align="right">{t('report.latenessCol')}</TableCell>
                  <TableCell align="right">{t('report.earlyCol')}</TableCell>
                  <TableCell align="right">{t('report.extraCol')}</TableCell>
                  <TableCell>{t('report.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((s, i) => (
                  <TableRow key={s.id || i} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'primary.light' }}>
                          {initials(s.worker_name)}
                        </Avatar>
                        <Typography variant="body2">{s.worker_name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.department_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.schedule_name
                          ? `${s.schedule_name} (${s.scheduled_start?.slice(0,5) || ''}–${s.scheduled_end?.slice(0,5) || ''})`
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LoginIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        <Typography variant="body2">{fmtTime(s.check_in)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LogoutIcon sx={{ fontSize: 14, color: 'error.main' }} />
                        <Typography variant="body2">{fmtTime(s.check_out)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CoffeeIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                        <Typography variant="body2">{fmtMinutes(s.break_minutes)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {fmtMinutes(s.net_worked_minutes)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {Number(s.late_minutes) > 0 ? (
                        <Typography variant="body2" color="warning.main" fontWeight={500}>
                          +{fmtMinutes(s.late_minutes)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {Number(s.early_checkout_minutes) > 0 ? (
                        <Typography variant="body2" color="warning.main" fontWeight={500}>
                          -{fmtMinutes(s.early_checkout_minutes)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {Number(s.overtime_minutes) > 0 ? (
                        <Typography variant="body2" color="info.main" fontWeight={500}>
                          +{fmtMinutes(s.overtime_minutes)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        status={s.compliance_status}
                        lateMin={s.late_minutes}
                        earlyMin={s.early_checkout_minutes}
                        overtimeMin={s.overtime_minutes}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {/* Absent workers */}
                {absentWorkers.map((w) => (
                  <TableRow key={`absent-${w.Id || w.id}`} hover sx={{ opacity: 0.6 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'grey.300' }}>
                          {initials(`${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`)}
                        </Avatar>
                        <Typography variant="body2">
                          {w.FirstName || w.first_name || ''} {w.LastName || w.last_name || ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {w.DepartmentName || w.department_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t('report.absentLabel')} color="error" size="small" />
                    </TableCell>
                  </TableRow>
                ))}

                {filtered.length === 0 && absentWorkers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {t('report.noDataForDate')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
